import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { observer } from "mobx-react-lite";
import { MdZoomIn, MdZoomOut } from "react-icons/md";
import {
  Container,
  CardPlant,
  CardSensor,
  Status,
  StatusText,
  Situations,
  Chip,
} from "./index.style";
import { MonitoringCard } from "../../../pages/sensorsDashboard/components/MonitoringCard";
import { useFetch } from "../../../../components/hooks/useFetch";
import { Prefix } from "../../../../services/prefix";
import Loading from "../../../../components/loader/loading";

const STATUS_CONFIG = {
  SUCCESS: { color: "#28a745", icon: "/media/icons/green-light.svg" },
  WARNING: { color: "#ffc107", icon: "/media/icons/yellow-light.svg" },
  DANGER: { color: "#dc3545", icon: "/media/icons/red-light.svg" },
  DARK: { color: "#6c757d", icon: "/media/icons/black-light.svg" },
};

const DetailPlan = ({ openModalPlan, toggleModalPlan, unit }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const [expand, setExpand] = useState(false);
  const [filterPlan, setFilterPlan] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0.7);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
  const [hoveredContractItemId, setHoveredContractItemId] = useState(null);
  const [selectedContractItemId, setSelectedContractItemId] = useState(null);

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2;

  const pointsPlant = useFetch(
    `${Prefix.replace(/[\\"]/g, "")}/sismogran/map/points/${unit.unit_plant_id}`
  );

  const contracItems = useFetch(
    `${Prefix.replace(/[\\"]/g, "")}/sismogran/map/units/${unit.id}/rooms`
  );

  const points = useMemo(() => {
    const data = pointsPlant?.data?.model || [];
    return data.map(item => ({
      id: item.id,
      x: Number(item.x),
      y: Number(item.y),
      name: item.nickname || "Sensor",
      contractItemId: item.contract_item_id,
      status: item.situation || "DARK",
      situations: item.situations || {},
    }));
  }, [pointsPlant.data]);

  const situations = useMemo(() => {
    return points.reduce(
      (acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
      },
      { SUCCESS: 0, WARNING: 0, DANGER: 0, DARK: 0 }
    );
  }, [points]);

  const filteredPoints = useMemo(() => {
    return filterPlan ? points.filter(p => p.status === filterPlan) : points;
  }, [points, filterPlan]);

  const contractItemsMap = useMemo(() => {
    const rooms = contracItems?.data?.models || [];
    const map = {};
    rooms.forEach(room => {
      room.contractItems.forEach(ci => {
        map[ci.id] = ci;
      });
    });
    return map;
  }, [contracItems.data]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const width = image.width * zoomLevel;
    const height = image.height * zoomLevel;
    const centerX = (canvas.width - width) / 2 + offset.x;
    const centerY = (canvas.height - height) / 2 + offset.y;

    ctx.drawImage(image, centerX, centerY, width, height);

    filteredPoints.forEach(p => {
      const px = (p.x / image.width) * width + centerX;
      const py = (p.y / image.height) * height + centerY;

      let radius = 9 * zoomLevel;
      if (p.contractItemId === selectedContractItemId) radius = 18 * zoomLevel;
      else if (p.contractItemId === hoveredContractItemId)
        radius = 14 * zoomLevel;

      ctx.beginPath();
      ctx.arc(px, py, radius, 0, Math.PI * 2);
      ctx.fillStyle =
        STATUS_CONFIG[p.status]?.color || STATUS_CONFIG.DARK.color;
      ctx.fill();
    });
  }, [
    zoomLevel,
    offset,
    filteredPoints,
    hoveredContractItemId,
    selectedContractItemId,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const img = new Image();
    img.src = unit?.unit_plant_path || "/media/images/plant-placeholder.png";
    img.width = 1200;
    img.height = 760;

    canvas.width = 1200;
    canvas.height = 760;

    img.onload = () => {
      imageRef.current = img;
      const loop = () => {
        drawCanvas();
        requestAnimationFrame(loop);
      };
      loop();
    };
  }, [drawCanvas, unit]);

  const handleWheel = e => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoomLevel(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  };

  const handleMouseDown = e => {
    setIsDragging(true);
    setLastMouse({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsDragging(false);

  const getPointAtPosition = (mx, my) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return null;

    const width = image.width * zoomLevel;
    const height = image.height * zoomLevel;
    const centerX = (canvas.width - width) / 2 + offset.x;
    const centerY = (canvas.height - height) / 2 + offset.y;

    let found = null;

    filteredPoints.forEach(p => {
      const px = (p.x / image.width) * width + centerX;
      const py = (p.y / image.height) * height + centerY;
      const radius = 14 * zoomLevel;
      if (Math.hypot(mx - px, my - py) < radius) found = p;
    });

    return found;
  };

  const handleMouseMove = e => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const my = ((e.clientY - rect.top) / rect.height) * canvas.height;

    const hoveredPoint = getPointAtPosition(mx, my);

    if (hoveredPoint) {
      canvas.style.cursor = "pointer";
      setHoveredContractItemId(hoveredPoint.contractItemId);
    } else {
      setHoveredContractItemId(null);
      canvas.style.cursor = isDragging ? "grabbing" : "grab";
    }

    if (isDragging) {
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastMouse({ x: e.clientX, y: e.clientY });
    }
  };

  const handleClick = e => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const my = ((e.clientY - rect.top) / rect.height) * canvas.height;
    const point = getPointAtPosition(mx, my);

    if (point?.contractItemId) {
      setSelectedContractItemId(prev =>
        prev === point.contractItemId ? null : point.contractItemId
      );
    } else {
      setSelectedContractItemId(null);
    }
  };

  const selectedContractItem = selectedContractItemId
    ? contractItemsMap[selectedContractItemId]
    : null;

  return (
    <Modal show={openModalPlan} onHide={toggleModalPlan} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ width: "100%" }}>
          <div className="d-flex justify-content-between align-items-center">
            <span>{unit?.name || "Mapa da Planta"}</span>
            <Situations>
              {Object.keys(STATUS_CONFIG).map(status => (
                <Chip
                  key={status}
                  situation={status}
                  active={filterPlan === status}
                  onClick={() => {
                    setFilterPlan(prev => (prev === status ? null : status));
                    setSelectedContractItemId(null);
                  }}
                >
                  <Status src={STATUS_CONFIG[status].icon} />
                  <StatusText>{situations[status]}</StatusText>
                </Chip>
              ))}
            </Situations>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container
          expand={expand}
          style={{
            display: "flex",
            width: "100%",
            gap: 12,
          }}
        >
          {/* Lado esquerdo - Canvas */}
          <CardPlant
            expand={expand}
            style={{
              flex: 1, // ocupa todo o espaço restante
              position: "relative",
            }}
          >
            {pointsPlant.loading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                <Spinner animation="border" />
              </div>
            )}
            <canvas
              ref={canvasRef}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onClick={handleClick}
              onMouseLeave={() => {
                setIsDragging(false);
                setHoveredContractItemId(null);
              }}
              style={{ cursor: "grab", width: "100%", height: "100%" }}
            />
          </CardPlant>

          {/* Lado direito - CardSensor fixo */}
          <div
            style={{
              flex: "0 0 420px", // largura fixa em 320px
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardSensor expand={expand} style={{ width: "100%" }}>
              {contracItems.loading ? (
                <Loading />
              ) : selectedContractItem ? (
                <MonitoringCard
                  key={selectedContractItem.id}
                  md={12}
                  item={{
                    id: selectedContractItem.id,
                    nickname: selectedContractItem.nickname,
                    situation: selectedContractItem.situation,
                    sensors: selectedContractItem.sensors || [],
                    updated_at: selectedContractItem.readed_at
                  }}
                  style={{ background: "transparent" }}
                />
              ) : (
                Object.values(contractItemsMap).map(ci => {
                  return (
                    <MonitoringCard
                      key={ci.id}
                      md={12}
                      item={{
                        id: ci.id,
                        nickname: ci.nickname,
                        situation: ci.situation,
                        sensors: ci.sensors || [],
                        updated_at: ci.readed_at
                      }}
                      style={{ background: "transparent" }}
                    />
                  )
                })
              )}
            </CardSensor>
          </div>
        </Container>

        {/* Zoom buttons */}
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setZoomLevel(z => Math.min(ZOOM_MAX, z + 0.1))}
          >
            <MdZoomIn />
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setZoomLevel(z => Math.max(ZOOM_MIN, z - 0.1))}
          >
            <MdZoomOut />
          </Button>
        </div>
      </Modal.Body>


    </Modal>
  );
};

export default observer(DetailPlan);
